import { t } from '../../../../lib/i18n';
import { SessionBidLots } from './session-bid-lots';
import type { ScheduleSessionView } from './session-schedule-types';

/**
 * The selected-day panel — admin port of
 * `apps/auction/.../components/ScheduleDayPanel.tsx`.
 *
 * Two deliberate omissions from the web original:
 *  - no `myBidsFor` row: the admin operator's view has no "your bids" concept.
 *  - no destination link on each session or the footer: the web original
 *    points at `/cars`, a route the admin SPA does not have (see
 *    `SessionListView`'s note for the full reasoning).
 */

interface SessionDayPanelProps {
  /** Eyebrow above the date: «Сьогодні» / «Завтра» / «Через N дн.». */
  eyebrow: string;
  /** «16 вересня, Ср». */
  title: string;
  /** «2 аукціони · 42 лоти · відправлення автовоза». */
  subtitle: string;
  views: ScheduleSessionView[];
  /** Copy for the empty state's second line — the next closing after this day. */
  nextHint: string;
  /** Per-session line: «42 лоти · закривається сьогодні». */
  metaFor: (_view: ScheduleSessionView) => string;
}

export function SessionDayPanel({ eyebrow, title, subtitle, views, nextHint, metaFor }: SessionDayPanelProps) {
  return (
    <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-5 py-[18px]">
      <p className="font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase">{eyebrow}</p>
      <h2 className="mt-1.5 text-[22px] font-semibold tracking-[-0.02em]">{title}</h2>
      <p className="mt-1 text-[13px] text-cf-ink-muted">{subtitle}</p>

      {views.length === 0 && (
        <div className="mt-4 rounded-cf-lg border border-dashed border-cf-line p-5 text-center">
          <p className="text-[14px] font-semibold">{t('auctionSchedule.day.empty')}</p>
          <p className="mt-1 text-[12px] leading-[1.5] text-cf-ink-subtle">{nextHint}</p>
        </div>
      )}

      {views.length > 0 && (
        <div className="mt-3.5 flex flex-col gap-2">
          {views.map((view) => (
            <div
              key={view.session.id}
              className="flex flex-col gap-1.5 rounded-cf-md border border-cf-line-soft border-l-[3px] px-3.5 py-3 text-cf-ink"
              style={{ borderLeftColor: view.dot }}
            >
              <span className="flex items-baseline justify-between gap-2">
                <strong className="text-[14px]">{view.session.house.name}</strong>
                <span className="font-mono text-[13px]">{view.session.time}</span>
              </span>
              <span className="text-[12px] text-cf-ink-subtle">{metaFor(view)}</span>
              <SessionBidLots lots={view.session.bidLots} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
